import { useEffect, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Alert, Button, Grid, Space, Typography } from 'antd';
import { ExperimentOutlined, ReloadOutlined, RobotOutlined, StarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PageLoader } from '@shared/common/components/ui/busy/busy-indicator';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { modelLine } from '@/components/ModelPickerDrawer';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { RESULT_STATE_LABEL, type ResultStateName } from '@/api/runEvidence';
import type { EvaluationWorklist, WorklistItem } from '@/api/evaluationWorklist';
import { EvaluationModelRole, getModelPlan, type EvaluationModelPlanView } from '@/api/scenarioIntelligence';
import { GroupHead, LensChip, STATE_ACCENT, STATE_TONE } from '@/features/validations/RunReadingPanel';
import { CoverageCard } from './CoverageCard';
import { useWorkspace } from './EvaluationWorkspace';
import { runLinksFor } from './paths';
import {
  STATE_HINT,
  STATE_ORDER,
  actionLabel,
  countOf,
  countRule,
  countsAgree,
  exploratorySentence,
  groupByState,
  itemMeta,
  notAssessedSentence,
  runnable,
  runningTitle,
} from './worklistWords';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// Overview — Scenario Intelligence screen 01, frame A (and frame C's states). It answers "what needs me?" for one
// agent: the five states counted once over the library's current versions (they add up), drafts apart as
// Exploratory, then the worklist ordered by what needs a person — each row one line of why and one next action —
// beside coverage by category with its denominators and the model plan. No score, no percentage, no green bar for
// the agent. Every count and state is the server's (EvaluationWorklistRules); this screen only words them.
// ─────────────────────────────────────────────────────────────────────────────

type Lens = ResultStateName | 'Exploratory' | 'All';
const PASSED_SHOWN = 0;

const KIND_PILL: Record<string, string> = {
  EditedSinceRun: 'Edited since run',
  NeverRun: 'Never run',
  EarlierAgentVersion: 'Earlier agent version',
  TooOld: 'Older than 30 days',
  VersionNotRecorded: 'Version not recorded',
  AgentVersionUnreadable: 'Agent version unread',
  DidNotComplete: 'Did not complete',
  NotRead: 'Not read',
  Quarantined: 'Quarantined',
};

const at = (iso: string | null | undefined) => (iso ? dayjs(iso).format('D MMM HH:mm') : null);

export const OverviewTab = observer(function OverviewTab() {
  const ws = useWorkspace();
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;

  if (ws.agentId == null) {
    if (ws.resolving)
      return (
        <SectionCard>
          <PageLoader label={`Finding the agent last evaluated in ${ws.practiceLabel}`} hint="The workspace opens on the agent of this practice's newest run." />
        </SectionCard>
      );
    return <NoAgentChosen />;
  }

  const { data, error } = ws.worklist;
  const name = ws.agent?.canonicalName || ws.agent?.displayName || data?.agentName || `agent #${ws.agentId}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="si-overview">
      <AgentBar worklist={data} isDark={isDark} />
      {!data && error && <CouldNotLoad message={error} />}
      {!data && !error && (
        <SectionCard>
          <PageLoader
            label={`Loading the evaluation worklist for ${name}`}
            hint="Reading this practice's latest run, the scenario library and each scenario's current version."
          />
        </SectionCard>
      )}
      {data && data.counts.library + data.exploratory.drafts === 0 && <NoScenarios name={name} />}
      {data && data.counts.library + data.exploratory.drafts > 0 && <Worklist data={data} isDark={isDark} narrow={narrow} />}
    </div>
  );
});

// ── The agent bar ────────────────────────────────────────────────────────────

const AgentBar = observer(function AgentBar({ worklist, isDark }: { worklist: EvaluationWorklist | null; isDark: boolean }) {
  const ws = useWorkspace();
  const a = ws.agent;
  const model = ws.agentModel;
  const links = runLinksFor('/evaluation');
  const latest = worklist?.latestRunId;
  const narrow = !Grid.useBreakpoint().md;
  // The agent's own model, as configured — shown, never chosen here (D-SI-4). Nothing is said when it could not be read.
  const modelText = model ? modelLine({ deployment: model.deploymentName, name: model.modelName, apiTitle: model.apiTitle }) || null : null;
  return (
    <div
      data-testid="si-agent-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '10px 14px',
        borderRadius: 12,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
      }}
    >
      {!narrow && (
        <span
          aria-hidden
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            background: isDark ? `${colors.ink[700]}40` : colors.ink[50],
            color: isDark ? colors.ink[100] : colors.ink[600],
            fontSize: 16,
          }}
        >
          <RobotOutlined />
        </span>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 240px', minWidth: 0 }}>
        <Text strong style={{ marginRight: 2 }}>{a?.displayName || worklist?.agentName || `Agent #${ws.agentId}`}</Text>
        {a?.canonicalName && <Tag mono>{a.canonicalName}</Tag>}
        <Tag>{`#${ws.agentId}`}</Tag>
        <Tag>{ws.practiceLabel}</Tag>
        {modelText && <Tag>{`Runs on ${modelText}`}</Tag>}
        {worklist &&
          (latest != null ? (
            <Tag>
              <Link to={links.run(latest)}>{`Latest run #${latest}`}</Link>
              {worklist.latestRunUtc ? ` · ${at(worklist.latestRunUtc)}` : ''}
            </Tag>
          ) : (
            <Tag>No run in this practice yet</Tag>
          ))}
      </div>
      <Button size="small" onClick={ws.chooseAgent}>
        Change agent
      </Button>
    </div>
  );
});

function Tag({ children, mono }: { children: ReactNode; mono?: boolean }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 8px',
        borderRadius: 6,
        fontSize: 12,
        lineHeight: '20px',
        fontFamily: mono ? MONO : undefined,
        background: isDark ? colors.slate[700] : colors.slate[100],
        color: isDark ? colors.slate[200] : colors.slate[700],
        maxWidth: '100%',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </span>
  );
}

// ── Frame C states ───────────────────────────────────────────────────────────

const NoAgentChosen = observer(function NoAgentChosen() {
  const ws = useWorkspace();
  return (
    <SectionCard>
      <BrandedEmpty
        icon={<ExperimentOutlined />}
        title="Choose an agent to evaluate"
        description={
          <>
            {`Scenario Intelligence works one agent at a time. Any agent of ${ws.practiceLabel}, or a shared agent, can be evaluated here.`}
            {ws.resolveError ? ` The agent last evaluated here could not be looked up: ${ws.resolveError}` : ' No run has acted in this practice yet, so there is no agent to open on.'}
          </>
        }
        action={
          <Button type="primary" onClick={ws.chooseAgent}>
            Choose an agent…
          </Button>
        }
      />
    </SectionCard>
  );
});

const NoScenarios = observer(function NoScenarios({ name }: { name: string }) {
  const ws = useWorkspace();
  return (
    <SectionCard>
      <BrandedEmpty
        icon={<ExperimentOutlined />}
        title={`No scenarios for ${name} yet`}
        description="Generate proposes scenarios from the agent's own configuration for you to review; nothing counts until a person adds it to the library. Reviewed requirement packages arrive with SI-05b, so none is proposed here yet."
        action={
          <Space wrap>
            <Button type="primary" icon={<StarOutlined />} onClick={() => ws.openGenerate()}>
              Generate scenarios
            </Button>
            <Link to={`/evaluation/scenarios?agent=${ws.agentId}`}>
              <Button>Write one by hand</Button>
            </Link>
          </Space>
        }
      />
    </SectionCard>
  );
});

const CouldNotLoad = observer(function CouldNotLoad({ message }: { message: string }) {
  const ws = useWorkspace();
  return (
    <Alert
      type="error"
      showIcon
      data-testid="si-load-error"
      title="Could not load the worklist"
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>{`The server said: "${message}" Switch to the practice that owns this agent, or choose an agent of ${ws.practiceLabel}.`}</span>
          <Space wrap>
            <Button size="small" icon={<ReloadOutlined />} onClick={ws.reload}>
              Retry
            </Button>
            <Button size="small" onClick={ws.chooseAgent}>
              Choose an agent…
            </Button>
          </Space>
        </div>
      }
    />
  );
});

// ── Frame A ──────────────────────────────────────────────────────────────────

const Worklist = observer(function Worklist({ data, isDark, narrow }: { data: EvaluationWorklist; isDark: boolean; narrow: boolean }) {
  const ws = useWorkspace();
  const [lens, setLens] = useState<Lens>('All');
  const [showNotAssessed, setShowNotAssessed] = useState(false);
  const [showPassed, setShowPassed] = useState(PASSED_SHOWN > 0);
  const [showDrafts, setShowDrafts] = useState(false);
  const groups = groupByState(data.items);
  const c = data.counts;
  const e = data.exploratory;
  const agree = countsAgree(data);
  const toRun = runnable(data.items);
  const active = c.library + e.drafts;
  const links = runLinksFor('/evaluation');

  // A new agent or practice starts unfiltered.
  useEffect(() => setLens('All'), [data.agentId, data.merchantId]);

  const shows = (state: Lens) => lens === 'All' || lens === state;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {data.running && <RunningAlert data={data} />}
      {ws.worklist.refreshError && (
        <Alert
          type="warning"
          showIcon
          data-testid="si-stale"
          title={`Showing the worklist as of ${ws.worklist.loadedAt ? dayjs(ws.worklist.loadedAt).format('HH:mm') : 'its last read'}`}
          description={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span>{`Refreshing failed: ${ws.worklist.refreshError} The counts below may be out of date.`}</span>
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={ws.reload}>
                  Retry
                </Button>
              </Space>
            </div>
          }
        />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: narrow ? 6 : 8, alignItems: 'center' }} role="group" aria-label="Filter the worklist by state">
        {STATE_ORDER.map((state) => (
          <LensChip
            key={state}
            label={RESULT_STATE_LABEL[state]}
            count={countOf(c, state)}
            color={STATE_ACCENT[state]}
            dashed={countOf(c, state) === 0}
            active={lens === state}
            onClick={() => setLens(lens === state ? 'All' : state)}
            isDark={isDark}
          />
        ))}
        {!narrow && <span aria-hidden style={{ width: 1, alignSelf: 'stretch', background: isDark ? colors.slate[700] : colors.slate[200], margin: '4px 2px' }} />}
        <LensChip
          label="Exploratory drafts"
          count={e.drafts}
          color={colors.violet[600]}
          dashed
          active={lens === 'Exploratory'}
          onClick={() => setLens(lens === 'Exploratory' ? 'All' : 'Exploratory')}
          isDark={isDark}
        />
      </div>
      <Text type="secondary" style={{ fontSize: 12, marginTop: -4, maxWidth: 900 }} data-testid="si-count-rule">
        {countRule(c)} Exploratory is a filter, not a state: it marks draft results that cannot count toward release, whatever their outcome.
      </Text>
      {!agree && (
        <Alert type="warning" showIcon title="The counts do not add up" description="The five states should count each library scenario once. Reload; if it stays, report it — the counts on this page are not to be trusted." />
      )}
      {data.notes.length > 0 && (
        <Alert type="info" showIcon title="Not in the counts" description={<span>{data.notes.join(' ')}</span>} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {shows('NeedsAttention') && groups.NeedsAttention.length > 0 && (
            <Group state="NeedsAttention" count={c.needsAttention}>
              {groups.NeedsAttention.map((i) => (
                <Row key={i.scenarioId} item={i} isDark={isDark} />
              ))}
            </Group>
          )}
          {shows('AwaitingReview') && groups.AwaitingReview.length > 0 && (
            <Group state="AwaitingReview" count={c.awaitingReview ?? 0}>
              {groups.AwaitingReview.map((i) => (
                <Row key={i.scenarioId} item={i} isDark={isDark} />
              ))}
            </Group>
          )}
          {shows('EvidenceIncomplete') && groups.EvidenceIncomplete.length > 0 && (
            <Group state="EvidenceIncomplete" count={c.evidenceIncomplete}>
              {groups.EvidenceIncomplete.map((i) => (
                <Row key={i.scenarioId} item={i} isDark={isDark} />
              ))}
            </Group>
          )}
          {shows('NotAssessed') && groups.NotAssessed.length > 0 && (
            <Group state="NotAssessed" count={c.notAssessed}>
              <RowShell accent={STATE_ACCENT.NotAssessed} isDark={isDark} testId="si-not-assessed">
                <span style={{ justifySelf: 'start' }}>
                  <StatusPill tone="slate">{`${c.notAssessed} scenario${c.notAssessed === 1 ? '' : 's'}`}</StatusPill>
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text style={{ fontSize: 13 }}>{notAssessedSentence(data.notAssessed)}</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                    {toRun.length === 0
                      ? 'None of them is waiting for a run.'
                      : toRun.length === 1
                        ? 'One run of this scenario gives it a result that counts.'
                        : `A run covers one scenario or all ${active} of the agent's active scenarios; running only these ${toRun.length} is not offered yet.`}
                  </Text>
                </div>
                <Space size={6} wrap style={{ justifySelf: narrow ? 'start' : 'end' }}>
                  {toRun.length === 1 && (
                    <Button size="small" onClick={() => ws.openRun(toRun[0].scenarioId)}>
                      Run this scenario…
                    </Button>
                  )}
                  {toRun.length > 1 && (
                    <Button size="small" onClick={() => ws.openRun()}>
                      {`Run all ${active}…`}
                    </Button>
                  )}
                  <Button size="small" type="link" onClick={() => setShowNotAssessed((v) => !v)}>
                    {showNotAssessed ? 'Hide' : `Show the ${c.notAssessed}`}
                  </Button>
                </Space>
              </RowShell>
              {showNotAssessed && groups.NotAssessed.map((i) => <Row key={i.scenarioId} item={i} isDark={isDark} />)}
            </Group>
          )}
          {shows('Passed') && groups.Passed.length > 0 && (
            <Group state="Passed" count={c.passed} hint={`${c.passed} · ${showPassed ? STATE_HINT.Passed : 'collapsed'}`}>
              <div style={{ ...cardStyle(isDark), padding: '10px 14px', fontSize: 13 }}>
                {`Every check that applied passed on the result that counts for ${c.passed === 1 ? 'this scenario' : `these ${c.passed}`}. `}
                <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => setShowPassed((v) => !v)}>
                  {showPassed ? 'Hide' : `Show ${c.passed}`}
                </Button>
              </div>
              {showPassed && groups.Passed.map((i) => <Row key={i.scenarioId} item={i} isDark={isDark} />)}
            </Group>
          )}
          {shows('Exploratory') && e.drafts > 0 && (
            <div data-testid="si-group-Exploratory">
              <GroupHead tone="violet" label="Exploratory drafts" hint={`not in the ${c.library} · their results never count toward release`} count={e.drafts} />
              <RowShell accent={colors.violet[500]} isDark={isDark}>
                <span style={{ justifySelf: 'start' }}>
                  <StatusPill tone="violet">{`${e.drafts} draft${e.drafts === 1 ? '' : 's'}`}</StatusPill>
                </span>
                <Text style={{ fontSize: 13, minWidth: 0 }}>{exploratorySentence(e)}</Text>
                <Space size={6} wrap style={{ justifySelf: narrow ? 'start' : 'end' }}>
                  <Button size="small" onClick={ws.openProposals}>
                    Open proposals
                  </Button>
                  <Button size="small" type="link" onClick={() => setShowDrafts((v) => !v)}>
                    {showDrafts ? 'Hide' : 'Show drafts'}
                  </Button>
                </Space>
              </RowShell>
              {showDrafts && groups.Exploratory.map((i) => <Row key={i.scenarioId} item={i} isDark={isDark} />)}
            </div>
          )}
          {lens !== 'All' && lens !== 'Exploratory' && groups[lens].length === 0 && (
            <Text type="secondary">{`No library scenario is ${RESULT_STATE_LABEL[lens].toLowerCase()} on its current version.`}</Text>
          )}
          {lens === 'Exploratory' && e.drafts === 0 && <Text type="secondary">This agent has no draft.</Text>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <CoverageCard data={data} compact />
          <ModelPlanCard />
          {data.evidenceRunIds.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {'Results that count come from '}
              {data.evidenceRunIds.map((id, i) => (
                <span key={id}>
                  {i > 0 ? ', ' : ''}
                  <Link to={links.run(id)}>{`run #${id}`}</Link>
                </span>
              ))}
              {`. Policy ${data.policyVersion}: a result counts when it tested this version of the agent, on the scenario as it is now, in this practice, in the last 30 days.`}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
});

function Group({ state, count, hint, children }: { state: ResultStateName; count: number; hint?: string; children: ReactNode }) {
  return (
    <div data-testid={`si-group-${state}`}>
      {hint ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <StatusPill tone={STATE_TONE[state]}>{RESULT_STATE_LABEL[state]}</StatusPill>
          <Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text>
        </div>
      ) : (
        <GroupHead tone={STATE_TONE[state]} label={RESULT_STATE_LABEL[state]} hint={STATE_HINT[state]} count={count} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

const cardStyle = (isDark: boolean) => ({
  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
  borderRadius: 12,
  background: isDark ? colors.slate[800] : '#FFFFFF',
});

function RowShell({ accent, isDark, children, testId }: { accent: string; isDark: boolean; children: ReactNode; testId?: string }) {
  const narrow = !Grid.useBreakpoint().md;
  return (
    <div
      data-testid={testId}
      style={{
        ...cardStyle(isDark),
        borderLeft: `3px solid ${accent}`,
        padding: '11px 14px 11px 12px',
        display: 'grid',
        gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '118px minmax(0, 1fr) auto',
        gap: narrow ? 6 : '4px 14px',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  );
}

const ROW_PILL: Record<ResultStateName, { label: string; tone: PillTone }> = {
  NeedsAttention: { label: 'Fail', tone: 'red' },
  AwaitingReview: { label: 'Awaiting review', tone: 'ink' },
  EvidenceIncomplete: { label: 'Not recorded', tone: 'amber' },
  NotAssessed: { label: 'Not assessed', tone: 'slate' },
  Passed: { label: 'Passed', tone: 'green' },
};

const Row = observer(function Row({ item, isDark }: { item: WorklistItem; isDark: boolean }) {
  const ws = useWorkspace();
  const narrow = !Grid.useBreakpoint().md;
  const label = actionLabel(item);
  const pill = item.state === 'NotAssessed' && item.notAssessedKind ? { label: KIND_PILL[item.notAssessedKind] ?? 'Not assessed', tone: 'slate' as PillTone } : ROW_PILL[item.state];
  const act = () => {
    if (item.nextAction === 'OpenFinding' && item.resultId != null) ws.openFinding(item.resultId);
    else if (item.nextAction === 'Run') ws.openRun(item.scenarioId);
  };
  return (
    <RowShell accent={item.exploratory ? colors.violet[500] : STATE_ACCENT[item.state]} isDark={isDark} testId="si-row">
      <span style={{ alignSelf: 'start', marginTop: 2 }}>
        <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, overflowWrap: 'anywhere' }}>{item.scenarioId}</div>
        <div style={{ fontSize: 13, marginTop: 2 }}>{item.why}</div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
          {itemMeta(item)}
        </Text>
      </div>
      {label ? (
        <Button size="small" style={{ justifySelf: narrow ? 'start' : 'end' }} onClick={act}>
          {label}
        </Button>
      ) : (
        <span />
      )}
    </RowShell>
  );
});

// ── Running ──────────────────────────────────────────────────────────────────

const RunningAlert = observer(function RunningAlert({ data }: { data: EvaluationWorklist }) {
  const job = data.running!;
  const links = runLinksFor('/evaluation');
  const started = at(job.startedUtc ?? job.submittedUtc);
  const still = data.latestRunId != null ? `still run #${data.latestRunId}` : 'from before this run';
  return (
    <Alert
      type="info"
      showIcon
      data-testid="si-running"
      title={runningTitle(job)}
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>
            {`The worklist below is ${still} and changes when ${job.runId != null ? `#${job.runId}` : 'this run'} completes. `}
            {`${job.startedUtc ? 'Started' : 'Asked for'} ${started ?? ''}${job.startedByViewer ? ' by you' : ''}${job.scenarioId ? ` · one scenario: ${job.scenarioId}` : ''}.`}
          </span>
          {job.runId != null && (
            <Space>
              <Link to={links.run(job.runId)}>
                <Button size="small">{`Follow run #${job.runId}`}</Button>
              </Link>
            </Space>
          )}
        </div>
      }
    />
  );
});

// ── Model plan ───────────────────────────────────────────────────────────────

const ModelPlanCard = observer(function ModelPlanCard() {
  const ws = useWorkspace();
  const { merchantContextStore } = useStore();
  const merchantId = merchantContextStore.selectedMerchantId;
  const [plan, setPlan] = useState<EvaluationModelPlanView | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setPlanError(null);
    getModelPlan(merchantId)
      .then((p) => !cancelled && setPlan(p))
      .catch((err: Error) => !cancelled && setPlanError(err.message));
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  const role = (r: EvaluationModelRole) => plan?.roles.find((x) => x.role === r);
  const roleLine = (r: EvaluationModelRole) => {
    const v = role(r);
    if (!v) return planError ? 'Not read' : '…';
    return modelLine(v.row) ?? v.deployment ?? (v.mayBeOff ? 'Off' : 'Not set');
  };
  const model = ws.agentModel;
  const evaluator = role(EvaluationModelRole.Evaluator);

  return (
    <SectionCard
      title="Model plan for runs"
      actions={
        <Button size="small" type="link" onClick={ws.openModels}>
          Evaluation models…
        </Button>
      }
    >
      <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, auto) minmax(0, 1fr)', gap: '8px 14px', margin: 0, fontSize: 13 }}>
        <dt style={{ color: colors.slate[500] }}>Agent model</dt>
        <dd style={{ margin: 0 }}>
          {(model && modelLine({ deployment: model.deploymentName, name: model.modelName, apiTitle: model.apiTitle })) || 'Not read'}
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>from the agent's configuration, not selectable here</Text>
        </dd>
        <dt style={{ color: colors.slate[500] }}>Evaluator</dt>
        <dd style={{ margin: 0 }}>
          {roleLine(EvaluationModelRole.Evaluator)}
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>
            {`setting${evaluator?.qualification ? ` · ${evaluator.qualification.toLowerCase()}` : ''} · no run uses it yet: results today come from deterministic checks only`}
          </Text>
        </dd>
        <dt style={{ color: colors.slate[500] }}>Scenario model</dt>
        <dd style={{ margin: 0 }}>
          {roleLine(EvaluationModelRole.Scenario)}
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>setting · drafts Generate's proposals</Text>
        </dd>
      </dl>
      {(planError || role(EvaluationModelRole.Evaluator)?.problem) && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
          {planError ? `The model plan could not be read: ${planError}` : role(EvaluationModelRole.Evaluator)?.problem}
        </Text>
      )}
    </SectionCard>
  );
});
